import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/Button'
import { useChallenges } from '../ChallengeProvider'
import { useJoinedChallenges } from '../hooks/useJoinedChallenges'
import type { Challenge } from '@/types/challenge'

interface ChallengeSelectionStepProps {
  selectedChallenge: Challenge | null
  onSelect: (challenge: Challenge) => void
  onNext: () => void
  onCustomChallenge: () => void
}

export function ChallengeSelectionStep({ 
  selectedChallenge, 
  onSelect, 
  onNext, 
  onCustomChallenge 
}: ChallengeSelectionStepProps) {
  const { t } = useTranslation()
  const { challenges, categories, isLoading, isError, error } = useChallenges()
  const { data: joinedChallengesData, isLoading: isLoadingJoined } = useJoinedChallenges()
  

  

  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [isCustomSelected, setIsCustomSelected] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Remove the static categoryMapping

  // Filter challenges based on category, search query, and already joined challenges
  const filteredChallenges = useMemo(() => {
    let challengesList = challenges

    // Filter out challenges that the user has already joined
    if (joinedChallengesData?.hasJoinedChallenge) {
      challengesList = challengesList.filter(challenge => 
        !joinedChallengesData.hasJoinedChallenge(challenge.id)
      )
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      challengesList = challengesList.filter(challenge => challenge.category === selectedCategory)
    }

    // Filter by search query (name and description)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim()
      challengesList = challengesList.filter(challenge => 
        challenge.name.toLowerCase().includes(query) ||
        (challenge.description && challenge.description.toLowerCase().includes(query))
      )
    }

    return challengesList
  }, [challenges, selectedCategory, searchQuery, joinedChallengesData])

  const handleChallengeSelect = (challenge: Challenge) => {
    onSelect(challenge)
    setIsCustomSelected(false)
    setIsTransitioning(true)
    
    // Auto-redirect to next step for challenge selection
    setTimeout(() => {
      onNext()
    }, 300) // Small delay for visual feedback
  }

  const handleCustomSelect = () => {
    setIsCustomSelected(true)
    onCustomChallenge()
    setIsTransitioning(true)
    
    // Auto-redirect to next step for custom challenge too
    setTimeout(() => {
      onNext()
    }, 300) // Small delay for visual feedback
  }





  // Show loading state
  if (isLoading || isLoadingJoined || !categories) {
    return (
      <div className="space-y-8">
        {/* Header Skeleton */}
        <div className="text-center space-y-3">
          <div className="h-8 bg-gradient-to-r from-bg-alt to-border rounded-full w-80 mx-auto animate-pulse"></div>
          <div className="h-5 bg-gradient-to-r from-border to-bg-alt rounded-full w-96 mx-auto animate-pulse"></div>
        </div>

        {/* Search Skeleton */}
        <div className="h-12 bg-gradient-to-r from-bg-alt to-border rounded-xl animate-pulse"></div>

        {/* Categories Skeleton */}
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-10 w-24 bg-gradient-to-r from-border to-bg-alt rounded-full animate-pulse"></div>
          ))}
        </div>

        {/* Challenges Skeleton */}
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gradient-to-r from-bg-alt to-border rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    )
  }

  // Show error state with fallback
  if (isError && challenges.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="bg-gradient-to-br from-bg-alt to-border/50 border border-border rounded-xl p-8 max-w-md mx-auto">
          <div className="text-6xl mb-4">😕</div>
          <h3 className="text-xl font-semibold text-text mb-2">{t('errors.oopsSomethingWentWrong')}</h3>
          <p className="text-text-muted mb-4">
            {error?.message || t('errors.failedToLoadChallenges')}
          </p>
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={() => window.location.reload()}
            className="bg-surface hover:bg-bg-alt text-text border-border"
          >
            🔄 {t('common.tryAgain')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Enhanced Header */}
      <div className="text-center relative">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-text to-text-muted bg-clip-text text-transparent mb-3">
          {t('challenges.chooseYourChallenge')}
        </h2>
        <p className="text-lg text-text-muted max-w-lg mx-auto leading-relaxed">
          {t('challenges.selectFromCurated')}
        </p>
      </div>

      {/* Enhanced Search */}
      <div className="relative group">
        <input
          type="text"
          placeholder={t('challenges.searchForPerfectChallenge')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-12 pr-4 py-4 border-2 border-border rounded-xl bg-gradient-to-r from-surface to-bg dark:bg-bg-alt text-text placeholder-text-muted focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all duration-300 group-hover:shadow-lg"
        />
        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-muted group-focus-within:text-primary transition-colors">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-text transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Enhanced Categories */}
      <div className="space-y-4 text-sm">
        <h3 className="text-sm font-semibold text-text flex items-center gap-2">
          <span className="text-2xl">🗂️</span>
          {t('challenges.categories')}
        </h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`flex-shrink-0  flex items-center space-x-sm px-md py-xs rounded-full border transition-colors ${
              selectedCategory === 'all'
                ? 'border-primary bg-primary text-white dark:text-black'
                : 'border-border bg-surface text-text hover:border-border-hover'
            }`}
          >
            ✨ {t('challenges.allCategories')}
          </button>
          {categories && categories.length > 0 ? (
            categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-md py-xs rounded-full text-sm font-medium transition-all duration-300 transform hover:scale-105 flex items-center gap-2 border ${
                  selectedCategory === category.id
                  ? 'border-primary bg-primary text-white dark:text-black'
                  : 'border-border bg-surface text-text hover:border-border-hover'
                }`}
              >
                <span className="text-lg">{category.icon}</span>
                <span>{category.name}</span>
              </button>
            ))
          ) : (
            <div className="text-sm text-text-muted">
              {isLoading ? 'Loading categories...' : 'No categories available'}
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Challenge List */}
      <div className="space-y-6 text-sm">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-text flex items-center gap-2">
            <span className="text-2xl">🎯</span>
            {selectedCategory === 'all' 
              ? t('challenges.allChallenges')
              : `${categories.find(c => c.id === selectedCategory)?.name || 'General'} ${t('challenges.challenges')}`
            }
          </h3>
        </div>

        {filteredChallenges.length > 0 ? (
          <div className="grid gap-4">
            {filteredChallenges.map((challenge, index) => {
              // Use only the category ID from the database
              const categoryInfo = categories.find(c => c.id === challenge.category)
              return (
                <button
                  key={challenge.id}
                  onClick={() => handleChallengeSelect(challenge)}
                  disabled={isTransitioning}
                  style={{ animationDelay: `${index * 50}ms` }}
                  className={`group p-6 border-2 rounded-xl text-left transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl animate-fade-in border-border bg-gradient-to-br from-surface via-bg to-surface/50 hover:border-primary/50 hover:shadow-lg ${isTransitioning ? 'opacity-75' : ''}`}
                >
                  <div className="flex items-start">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-text text-lg group-hover:text-primary transition-colors">
                          {challenge.name}
                        </h4>
                      </div>
                      <p className="text-text-muted leading-relaxed mb-3">
                        {challenge.description}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-bg-alt to-border text-text-muted">
                          <span>{categoryInfo?.icon || '📁'}</span>
                          {categoryInfo?.name || 'General'}
                        </span>
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-primary/10 to-primary/5 text-primary">
                          ⏱️ {challenge.defaultFrequency.mode}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="bg-gradient-to-br from-bg-alt to-border/50 rounded-xl p-8 max-w-md mx-auto">
              <div className="text-6xl mb-4">
                {joinedChallengesData?.joinedChallengeIds.length === challenges.length ? '🎉' : '🔍'}
              </div>
              <h3 className="text-xl font-semibold text-text mb-2">
                {joinedChallengesData?.joinedChallengeIds.length === challenges.length 
                  ? t('challenges.allChallengesJoined')
                  : t('challenges.noChallengesFound')
                }
              </h3>
              <p className="text-text-muted mb-4">
                {joinedChallengesData?.joinedChallengeIds.length === challenges.length
                  ? t('challenges.allChallengesJoinedDescription')
                  : t('challenges.noChallengesFoundDescription')
                }
              </p>
              {joinedChallengesData?.joinedChallengeIds.length !== challenges.length && (
                <Button 
                  onClick={() => setSearchQuery('')}
                  variant="secondary"
                  size="sm"
                >
                  {t('common.clearSearch')}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Custom Challenge Option */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5 rounded-xl"></div>
        <button
          onClick={handleCustomSelect}
          disabled={isTransitioning}
          className={`relative w-full p-6 border-2 border-dashed rounded-xl text-center transition-all duration-300 transform hover:scale-[1.02] group ${
            isCustomSelected
              ? 'border-primary bg-gradient-to-br from-primary/10 via-primary/5 to-transparent ring-4 ring-primary/20'
              : 'border-border bg-gradient-to-br from-surface to-bg hover:border-primary/50 hover:shadow-lg'
          } ${isTransitioning ? 'opacity-75' : ''}`}
        >
          <div className="space-y-3">
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center text-2xl transition-all group-hover:scale-110 ${
              isCustomSelected
                ? 'bg-gradient-to-br from-primary to-primary/80 text-white dark:text-black'
                : 'bg-gradient-to-br from-bg-alt to-border text-text-muted group-hover:text-primary'
            }`}>
              ✨
            </div>
            <div>
              <h4 className="text-xl font-semibold text-text mb-2 group-hover:text-primary transition-colors">
                {t('challenges.createCustomChallenge')}
              </h4>
              <p className="text-text-muted">
                {t('challenges.createCustomChallengeDescription')}
              </p>
            </div>
          </div>
        </button>
      </div>


    </div>
  )
} 